import { useState, FC, useEffect } from 'react';

import { Save } from '@mui/icons-material';
import { Box, Typography, Button, CircularProgress, TextField } from '@mui/material';
import { useMutation, useQuery } from 'react-query';

import {
  addMissingDataApiV1MissingDataAddPost,
  getMissingDataApiV1MissingDataDataTypeGet,
  getUniqueFieldValuesApiV1MissingDataUniqueValuesDataTypeGet,
  MissingDataType,
  UniqueValueQueryType,
} from '../../client';
import { useAuth } from '../../services/auth/AuthContext';
import NotificationService from '../../services/notification/NotificationService';
import { extractSpreadsheetId } from '../../utils/googleSheetsUtils';
import { EditDataTable, UniqueValues } from '../common/EditDataTable';
import PageLayout from '../PageLayout/PageLayout';

interface ColumnDefinition {
  name: string;
  type: 'text' | 'number';
}

interface Row {
  [key: string]: string | number | null;
}

interface FixDataPageProps {
  title: string;
  columns: ColumnDefinition[];
  dataType: MissingDataType;
  spreadsheetId: string;
  spreadsheetRange: string;
  pageId: string;
}

export const FixDataPage: FC<FixDataPageProps> = ({
  title,
  columns,
  dataType,
  spreadsheetId,
  spreadsheetRange,
  pageId,
}) => {
  const { getToken } = useAuth();
  const [rows, setRows] = useState<Row[]>([]);
  const [isDataComplete, setIsDataComplete] = useState(false);
  const [sheetUrl, setSheetUrl] = useState(spreadsheetId);
  const [range, setRange] = useState(spreadsheetRange);
  const [uniqueValues, setUniqueValues] = useState<UniqueValues>({});

  // Fetch unique values for applicable fields
  useEffect(() => {
    const fetchUniqueValues = async () => {
      const token = await getToken();
      const uniqueFieldsMap: { [key: string]: UniqueValueQueryType } = {
        product_category: 'product_category',
        product_class: 'product_class',
        final_buyer: 'final_buyer',
        unit: 'unit',
        buying_department: 'buying_department',
        customer_group: 'customer_group',
        customer_category: 'customer_category',
      };

      const promises = columns
        .filter((col) => uniqueFieldsMap[col.name])
        .map(async (col) => {
          try {
            const response = await getUniqueFieldValuesApiV1MissingDataUniqueValuesDataTypeGet({
              path: { data_type: uniqueFieldsMap[col.name] as UniqueValueQueryType },
              headers: { Authorization: `Bearer ${token}` },
            });
            return { field: col.name, values: response.data?.values || [] };
          } catch (error) {
            NotificationService.error(`Error fetching unique values for ${col.name}: {error}`);
            return { field: col.name, values: [] };
          }
        });

      const results = await Promise.all(promises);
      const newUniqueValues = results.reduce(
        (acc, { field, values }) => ({
          ...acc,
          [field]: values.map((value) => ({
            value,
            isNew: false,
          })),
        }),
        {},
      );

      setUniqueValues(newUniqueValues);
    };

    fetchUniqueValues();
  }, [columns, getToken]);

  // Fetch missing data
  const { isLoading, error, data } = useQuery(['missingData', dataType], async () => {
    const token = await getToken();
    const response = await getMissingDataApiV1MissingDataDataTypeGet({
      path: { data_type: dataType },
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });

    if (response.error) {
      throw new Error(JSON.stringify(response.error));
    }

    return response.data;
  });

  // Show error notification if query fails
  useEffect(() => {
    if (error) {
      NotificationService.error(`Error loading data: ${String(error)}`);
    }
  }, [error]);

  // Submit data mutation
  const mutation = useMutation(
    async (dataToSubmit: Row[]) => {
      const token = await getToken();
      const response = await addMissingDataApiV1MissingDataAddPost({
        body: {
          data_type: dataType,
          items: dataToSubmit,
          spreadsheet_id: extractSpreadsheetId(sheetUrl) || '',
          spreadsheet_range: range,
        },
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (response.error) {
        throw new Error(JSON.stringify(response.error));
      }

      return response.data;
    },
    {
      onSuccess: (data) => {
        if (data.success) {
          NotificationService.success(
            `Successfully added ${data.items_added} items: ${data.message} - Refreshing page`,
          );
          // Refresh the page after 2 seconds to show updated data
          setTimeout(() => {
            window.location.reload();
          }, 2000);
        } else {
          NotificationService.error(`Failed to submit data: ${data.message}`);
        }
      },
      onError: (error) => {
        NotificationService.error(`Failed to submit data: ${error}`);
      },
    },
  );

  const handleDataChange = (updatedRows: Row[], complete: boolean) => {
    setRows(updatedRows);
    setIsDataComplete(complete);
  };

  const handleSubmit = () => {
    if (!isDataComplete) {
      NotificationService.warning('Please fill in all required fields before submitting');
      return;
    }

    const spreadsheetId = extractSpreadsheetId(sheetUrl);
    if (!spreadsheetId) {
      NotificationService.error(
        'Invalid Google Sheets URL. Please use a full URL containing a spreadsheet ID',
      );
      return;
    }

    if (!range) {
      NotificationService.error('Please provide a data range');
      return;
    }

    mutation.mutate(rows);
  };

  const renderContent = () => {
    // Show loading state
    if (isLoading || !data?.missing_items) {
      return (
        <Box sx={{ display: 'flex', justifyContent: 'center', my: 4 }}>
          <CircularProgress />
        </Box>
      );
    }

    // Show "no data to fix" state
    if (data.missing_items.length === 0) {
      return (
        <Box sx={{ display: 'flex', justifyContent: 'center', my: 4 }}>
          <Typography variant="body1" color="text.secondary">
            All data fixed
          </Typography>
        </Box>
      );
    }

    // Show data table and submit button
    return (
      <>
        <EditDataTable
          columns={columns}
          initialRows={data ? (data.missing_items as Row[]) : []}
          uniqueValues={uniqueValues}
          onDataChange={handleDataChange}
        />

        <Box sx={{ mt: 3, display: 'flex', justifyContent: 'flex-end' }}>
          <Button
            variant="contained"
            color="primary"
            startIcon={
              mutation.isLoading ? <CircularProgress size={20} color="inherit" /> : <Save />
            }
            onClick={handleSubmit}
            disabled={mutation.isLoading || !isDataComplete}
          >
            {mutation.isLoading ? 'Submitting...' : 'Submit Data'}
          </Button>
        </Box>
      </>
    );
  };

  return (
    <PageLayout pageId={pageId}>
      <Box sx={{ p: 3 }}>
        <Typography variant="h4" gutterBottom>
          {title}
        </Typography>

        {data?.missing_items && data.missing_items.length > 0 ? (
          <Box sx={{ mb: 3 }}>
            <TextField
              fullWidth
              label="Google Sheets URL"
              value={sheetUrl}
              onChange={(e) => setSheetUrl(e.target.value)}
              placeholder="https://docs.google.com/spreadsheets/d/SPREADSHEET_ID/edit"
              sx={{ mb: 2 }}
            />
            <TextField
              fullWidth
              label="Data Range"
              value={range}
              onChange={(e) => setRange(e.target.value)}
              placeholder="Sheet1!A1:D100"
            />
          </Box>
        ) : null}

        {renderContent()}
      </Box>
    </PageLayout>
  );
};

export default FixDataPage;
